import type { LiveClockState } from '@/domain/live-clock';
import { cn } from '@/lib/cn';
import { Badge } from '../common/badge';
import type { OddsFlash } from '../common/odds-button';
import { Icon } from '../common/icon';
import { LiveClockText } from '../live/LiveClockText';

export interface BetslipSelectionRowProps {
  selection: string;
  match: string;
  market: string;
  odds: string;
  /** Per-leg stake shown in Single mode. */
  stakeLabel?: string;
  showStake?: boolean;
  /** System mode: show banker toggle. */
  showBanker?: boolean;
  banked?: boolean;
  bankerDisabled?: boolean;
  /** Live in-play selection. */
  live?: boolean;
  /** Translated live badge label. */
  liveLabel?: string;
  homeScore?: string;
  awayScore?: string;
  /** Static clock fallback when liveClockState is unavailable. */
  clock?: string;
  liveClockState?: LiveClockState;
  /** WebsiteTranslates lookup for live period codes (`HalfTime`, `2HT`, …). */
  translatePeriod?: (key: string) => string;
  /** Last odds move direction from the live feed (arrow only, no flash background). */
  flash?: OddsFlash;
  /** Closed / suspended / locked odds. */
  blocked?: boolean;
  onBankerToggle?: () => void;
  onRemove?: () => void;
  onStakeChange?: (value: string) => void;
  className?: string;
}

/**
 * Figma BetslipSelectionRow — remove column, outcome / market / match, odds,
 * optional per-leg stake (Single) or banker toggle (System).
 * Live selections show badge + clock + score; closed odds show a lock.
 */
export function BetslipSelectionRow({
  selection,
  match,
  market,
  odds,
  stakeLabel,
  showStake = false,
  showBanker = false,
  banked = false,
  bankerDisabled = false,
  live = false,
  liveLabel = 'Live',
  homeScore,
  awayScore,
  clock,
  liveClockState,
  translatePeriod,
  flash = 'none',
  blocked = false,
  onBankerToggle,
  onRemove,
  onStakeChange,
  className,
}: BetslipSelectionRowProps) {
  const showScore = live && (homeScore !== undefined || awayScore !== undefined);
  const showClock = live && (Boolean(clock) || Boolean(liveClockState));
  const showFlash = flash !== 'none' && !blocked;

  return (
    <article
      className={cn(
        // shrink-0: parent scroll column is flex — without this rows compress and text overlaps.
        'flex shrink-0 items-stretch overflow-hidden rounded-md bg-bg-surface',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Remove selection"
        onClick={onRemove}
        className="flex w-11 shrink-0 items-center justify-center bg-bg-elevated text-icon-muted transition-colors hover:text-text-primary"
      >
        <Icon name="close" size={16} />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1 p-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-body-m font-bold text-text-primary">{selection}</p>
          <p className="truncate text-body-s text-text-secondary">{market}</p>
        </div>
        <p className="truncate text-body-s text-text-secondary">{match}</p>
        {live ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge tone="live">{liveLabel}</Badge>
            {showClock ? (
              <LiveClockText
                liveClockState={liveClockState}
                clock={clock}
                translatePeriod={translatePeriod}
                className="text-body-s text-text-secondary"
              />
            ) : null}
            {showScore ? (
              <span className="text-body-s font-bold tabular-nums text-text-primary">
                {homeScore ?? '—'}:{awayScore ?? '—'}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          {blocked ? (
            <span className="flex h-5 items-center text-icon-default" aria-label="Selection locked">
              <Icon name="lock" size={16} />
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <p className="text-odds tabular-nums text-text-primary">{odds}</p>
              {showFlash ? (
                <span
                  className={cn(
                    'text-[9px] leading-none font-bold',
                    flash === 'up' ? 'text-odds-up' : 'text-odds-down',
                  )}
                >
                  <span aria-hidden>{flash === 'up' ? '▲' : '▼'}</span>
                  <span className="sr-only">{flash === 'up' ? 'price up' : 'price down'}</span>
                </span>
              ) : null}
            </span>
          )}
          {showStake ? (
            <input
              type="text"
              inputMode="decimal"
              aria-label="Stake for selection"
              value={stakeLabel ?? ''}
              onChange={(event) => onStakeChange?.(event.target.value)}
              className="h-8 w-[72px] rounded-md bg-bg-elevated px-3.5 text-center text-body-m font-medium text-text-primary outline-none focus-visible:ring-1 focus-visible:ring-border-focus"
            />
          ) : null}
          {showBanker ? (
            <button
              type="button"
              aria-label={banked ? 'Remove banker' : 'Mark as banker'}
              aria-pressed={banked}
              disabled={bankerDisabled && !banked}
              onClick={onBankerToggle}
              className={cn(
                'flex h-9 min-w-[45px] items-center justify-center rounded-sm px-4 text-body-m font-bold transition-colors',
                banked
                  ? 'bg-action-primary text-text-on-action'
                  : 'bg-bg-elevated text-text-primary',
                bankerDisabled && !banked
                  ? 'cursor-not-allowed opacity-40'
                  : 'hover:opacity-90',
              )}
            >
              B
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
